"use client"

import { useState } from "react"
import {
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Switch,
  FormControlLabel,
  Button,
  Paper,
  Typography,
  Divider,
} from "@mui/material"
import LoadingButton from "../common/LoadingButton"
import { useAuth } from "../../contexts/AuthContext"

const UserForm = ({ user = {}, onSubmit, loading = false, organizations = [], isEdit = false }) => {
  const { user: currentUser } = useAuth()
  const isSuperAdmin = currentUser?.role === "superadmin"

  const [formData, setFormData] = useState({
    firstName: user.firstName || "",
    lastName: user.lastName || "",
    email: user.email || "",
    password: "",
    confirmPassword: "",
    role: user.role || "user",
    organizationId: user.organizationId || "",
    sendWelcomeEmail: !isEdit,
  })

  const handleChange = (e) => {
    const { name, value, checked } = e.target
    setFormData((prev) => ({
      ...prev,
      [name]: name === "sendWelcomeEmail" ? checked : value,
    }))
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    onSubmit(formData)
  }

  return (
    <Paper
      elevation={0}
      sx={{
        p: 4,
        borderRadius: 2,
        mb: 4,
        border: "1px solid",
        borderColor: "divider",
        bgcolor: "background.paper",
      }}
    >
      <Typography variant="h5" component="h2" gutterBottom>
        User Details
      </Typography>

      <form onSubmit={handleSubmit}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="First Name"
              name="firstName"
              value={formData.firstName}
              onChange={handleChange}
              required
              variant="outlined"
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Last Name"
              name="lastName"
              value={formData.lastName}
              onChange={handleChange}
              variant="outlined"
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Email Address"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              required
              variant="outlined"
              disabled={isEdit}
            />
          </Grid>

          {!isEdit && (
            <>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Password"
                  name="password"
                  type="password"
                  value={formData.password}
                  onChange={handleChange}
                  helperText="Leave blank to generate a random password"
                  variant="outlined"
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Confirm Password"
                  name="confirmPassword"
                  type="password"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  variant="outlined"
                  disabled={!formData.password}
                />
              </Grid>
            </>
          )}

          {!isEdit && (
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.sendWelcomeEmail}
                    onChange={handleChange}
                    name="sendWelcomeEmail"
                    color="primary"
                  />
                }
                label="Send welcome email with login details"
              />
            </Grid>
          )}

          <Grid item xs={12}>
            <Divider sx={{ my: 2 }} />
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel id="role-label">Role</InputLabel>
              <Select labelId="role-label" name="role" value={formData.role} onChange={handleChange} label="Role">
                <MenuItem value="user">Read Only</MenuItem>
                <MenuItem value="admin">Admin</MenuItem>
                {isSuperAdmin && <MenuItem value="superadmin">Super Admin</MenuItem>}
              </Select>
              <FormHelperText>Select the user's permission level</FormHelperText>
            </FormControl>
          </Grid>

          {isSuperAdmin && (
            <Grid item xs={12} md={6}>
              <FormControl fullWidth required>
                <InputLabel id="organization-label">Organization</InputLabel>
                <Select
                  labelId="organization-label"
                  name="organizationId"
                  value={formData.organizationId}
                  onChange={handleChange}
                  label="Organization"
                >
                  {organizations.map((org) => (
                    <MenuItem key={org._id} value={org._id}>
                      {org.name}
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>Select the user's organization</FormHelperText>
              </FormControl>
            </Grid>
          )}

          <Grid item xs={12} sx={{ mt: 3, display: "flex", justifyContent: "flex-end", gap: 2 }}>
            <Button variant="outlined" onClick={() => window.history.back()} sx={{ px: 3 }}>
              Cancel
            </Button>

            <LoadingButton type="submit" loading={loading} variant="contained" color="primary" sx={{ px: 4 }}>
              {isEdit ? "Update User" : "Create User"}
            </LoadingButton>
          </Grid>
        </Grid>
      </form>
    </Paper>
  )
}

export default UserForm

